import React, { useState, useEffect, useRef } from 'react';
import { Download, Play, Rocket, LifeBuoy, CheckCircle2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import CreativesLayout from '@/layouts/CreativesLayout';
import { directorApiClient } from '@/services/directorApi';
import { videoApiClient } from '@/services/videoApi';
import useAuthenticatedMediaUrl from '@/hooks/useAuthenticatedMediaUrl';
import CreativePreviewFallback from '@/components/deal/CreativePreviewFallback';
import LaunchOnCtvForm from '@/components/creatives/LaunchOnCtvForm';
import CreativeHelpForm from '@/components/creatives/CreativeHelpForm';
import { trackEvent } from '@/lib/analytics';

const ASPECT_LABELS = {
    '16:9': 'Landscape (16:9)',
    '9:16': 'Portrait (9:16)',
    '1:1': 'Square (1:1)',
};

const ReviewStep = ({
    videoUrl,
    videoJobId,
    realBusinessData,
    selectedBusiness,
    aspectRatio,
    duration,
    onReset,
    mockMode = false,
}) => {
    const businessName = realBusinessData?.name || selectedBusiness?.name || 'your business';
    const [videoConfig, setVideoConfig] = useState(null);
    const [posterFailed, setPosterFailed] = useState(false);
    const [ctvFormOpen, setCtvFormOpen] = useState(false);
    const [helpFormOpen, setHelpFormOpen] = useState(false);

    const normalizeMediaUrl = (url) => {
        if (typeof url !== 'string' || !url) return '';
        if (
            url.startsWith('http://')
            || url.startsWith('https://')
            || url.startsWith('blob:')
            || url.startsWith('data:')
        ) {
            return url;
        }
        if (url.startsWith('/')) {
            return `${videoApiClient.baseUrl}${url}`;
        }
        return url;
    };

    const previewVideoUrl = normalizeMediaUrl(videoUrl || (videoJobId ? videoApiClient.getAssetUrl(videoJobId) : ''));
    const previewPoster = normalizeMediaUrl(videoJobId ? videoApiClient.getThumbnailUrl(videoJobId) : '');
    const {
        resolvedUrl: playablePreviewVideoUrl,
        isLoading: isLoadingPreviewVideo,
    } = useAuthenticatedMediaUrl(previewVideoUrl);

    useEffect(() => {
        if (!videoJobId) return;
        if (mockMode) {
            setVideoConfig({ resolution: aspectRatio === '16:9' ? '1920x1080' : '1080x1920' });
            return;
        }
        directorApiClient.getVideoConfig(videoJobId)
            .then(res => setVideoConfig(res.result || res))
            .catch(() => {});
    }, [videoJobId, mockMode]);

    useEffect(() => {
        setPosterFailed(false);
    }, [previewPoster]);

    const resolution = videoConfig?.resolution;

    const handleDownload = () => {
        trackEvent('creative_download_clicked', { job_id: videoJobId });
        const slug = (businessName || 'creative')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '') || 'creative';
        const filename = `cornercast-${slug}.mp4`;
        // Prefer the authenticated blob URL so the browser forces a download
        // with a filename (cross-origin asset URLs ignore the download attr).
        const href = playablePreviewVideoUrl || previewVideoUrl;
        if (!href) return;
        const a = document.createElement('a');
        a.href = href;
        a.download = filename;
        a.rel = 'noopener';
        if (!playablePreviewVideoUrl) a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const RightPanelContent = (
        <div className="w-full h-full bg-black rounded-lg overflow-hidden flex items-center justify-center">
            {playablePreviewVideoUrl ? (
                <video
                    key={playablePreviewVideoUrl}
                    src={playablePreviewVideoUrl}
                    poster={previewPoster && !posterFailed ? previewPoster : undefined}
                    controls
                    autoPlay
                    muted
                    className="w-full h-full object-contain"
                />
            ) : previewPoster && !posterFailed ? (
                <img
                    src={previewPoster}
                    alt={`${businessName} preview`}
                    className="w-full h-full object-contain opacity-80"
                    onError={() => setPosterFailed(true)}
                />
            ) : (
                <CreativePreviewFallback label={isLoadingPreviewVideo ? 'Loading preview' : 'Preview unavailable'} />
            )}
        </div>
    );

    return (
        <CreativesLayout preview={RightPanelContent}>
            <div className="flex flex-col space-y-6 py-4">
                <div className="space-y-3">
                    <div className="flex items-center gap-2 text-teal mb-1">
                        <CheckCircle2 className="h-5 w-5" />
                        <span className="font-semibold text-sm uppercase tracking-wider">Generation Complete</span>
                    </div>
                    <h2 className="text-2xl font-semibold tracking-tight text-foreground">
                        Ready for Primetime.
                    </h2>
                    <p className="text-muted-foreground text-base leading-relaxed">
                        Your ad for <strong className="text-foreground">{businessName}</strong> has been generated and is ready to launch.
                    </p>
                </div>

                {/* Video Specs */}
                <div className="grid grid-cols-2 gap-4 text-sm border-y py-4">
                    {resolution && (
                        <div>
                            <span className="block text-muted-foreground mb-0.5">Resolution</span>
                            <span className="font-medium">{resolution}</span>
                        </div>
                    )}
                    <div>
                        <span className="block text-muted-foreground mb-0.5">Format</span>
                        <span className="font-medium">{ASPECT_LABELS[aspectRatio] || aspectRatio}</span>
                    </div>
                    <div>
                        <span className="block text-muted-foreground mb-0.5">Duration</span>
                        <span className="font-medium">{duration}s</span>
                    </div>
                    <div>
                        <span className="block text-muted-foreground mb-0.5">Output</span>
                        <span className="font-medium">MP4</span>
                    </div>
                </div>

                <div className="space-y-3 pt-2">
                    <Button
                        size="lg"
                        onClick={() => {
                            trackEvent('creative_launch_ctv_clicked', { job_id: videoJobId });
                            setCtvFormOpen(true);
                        }}
                        className="w-full text-base h-12 shadow-sm bg-coral hover:bg-coral-hover border-none text-white"
                    >
                        <Rocket className="h-5 w-5 mr-2" />
                        Launch on CTV Network
                    </Button>

                    <div className="grid grid-cols-2 gap-3">
                        {previewVideoUrl && (
                            <Button variant="outline" size="lg" onClick={handleDownload}>
                                <Download className="h-4 w-4 mr-2" />
                                Download
                            </Button>
                        )}
                        <Button variant="outline" size="lg" onClick={() => setHelpFormOpen(true)}>
                            <LifeBuoy className="h-4 w-4 mr-2" />
                            Help
                        </Button>
                    </div>
                </div>

                <div className="pt-6 text-center border-t mt-auto">
                    <Button variant="ghost" onClick={onReset} className="text-muted-foreground hover:text-foreground">
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Create Another Video
                    </Button>
                    {videoJobId && (
                        <p className="text-[10px] text-muted-foreground/30 mt-4 uppercase tracking-widest">
                            Job ID: {videoJobId}
                        </p>
                    )}
                </div>
            </div>

            <LaunchOnCtvForm
                open={ctvFormOpen}
                onOpenChange={setCtvFormOpen}
                creativeId={videoJobId}
                creativeUrl={previewVideoUrl}
                defaults={{
                    business_name: realBusinessData?.name || selectedBusiness?.name || '',
                    maps_or_website:
                        realBusinessData?.maps_url
                        || realBusinessData?.website_url
                        || realBusinessData?.website
                        || '',
                }}
            />

            <CreativeHelpForm
                open={helpFormOpen}
                onOpenChange={setHelpFormOpen}
                creativeId={videoJobId}
            />
        </CreativesLayout>
    );
};

export default ReviewStep;
